import React, { useState } from 'react';
import { CheckCircle, XCircle, Search } from 'lucide-react';

const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  return words.length === 1
    ? words[0].slice(0, 2).toUpperCase()
    : (words[0][0] + words[1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  '#2e7cba', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#0ea5e9', '#14b8a6', '#f97316',
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const CompanyList = ({ companies, selectedCompany, theme, styles, onSelect }) => {
  const [search, setSearch] = useState('');

  const filtered = companies.filter(c =>
    c.clientName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      background: theme === 'light' ? '#ffffff' : 'rgba(15,23,42,0.5)',
      border: theme === 'light' ? '1.5px solid #e8edf3' : '1.5px solid rgba(255,255,255,0.07)',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: theme === 'light' ? '0 2px 12px rgba(0,0,0,0.05)' : 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{
          fontSize: '15px',
          fontWeight: '700',
          color: theme === 'light' ? '#0f172a' : '#ffffff',
          margin: 0,
          letterSpacing: '-0.2px',
        }}>
          Companies
        </h3>
        <span style={{
          background: theme === 'light' ? 'rgba(46,124,186,0.1)' : 'rgba(46,124,186,0.2)',
          color: '#2e7cba',
          fontSize: '12px',
          fontWeight: '700',
          padding: '3px 9px',
          borderRadius: '20px',
        }}>
          {companies.length}
        </span>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '14px' }}>
        <Search size={14} style={{
          position: 'absolute',
          left: '11px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.35)',
          pointerEvents: 'none',
        }} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies..."
          style={{
            width: '100%',
            padding: '9px 12px 9px 32px',
            borderRadius: '9px',
            border: theme === 'light' ? '1.5px solid #e8edf3' : '1.5px solid rgba(255,255,255,0.08)',
            background: theme === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.04)',
            color: theme === 'light' ? '#0f172a' : '#ffffff',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* List */}
      <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: '2px' }}>
        {filtered.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '32px 0',
            color: theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.35)',
            fontSize: '13px',
          }}>
            No companies found
          </div>
        )}

        {filtered.map(company => {
          const isSelected = selectedCompany === company.clientName;
          const avatarColor = getAvatarColor(company.clientName);
          return (
            <div
              key={company.clientName}
              style={styles.companyCard(isSelected)}
              onClick={() => onSelect(company.clientName)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#2e7cba';
                  e.currentTarget.style.background = theme === 'light'
                    ? 'rgba(46,124,186,0.04)'
                    : 'rgba(46,124,186,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = theme === 'light' ? '#e8edf3' : 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.background = theme === 'light' ? '#fafbfc' : 'rgba(255,255,255,0.03)';
                }
              }}
            >
              {/* Avatar */}
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: isSelected
                  ? avatarColor
                  : `${avatarColor}22`,
                color: isSelected ? '#ffffff' : avatarColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: '800',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}>
                {getInitials(company.clientName)}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  ...styles.companyName,
                  fontSize: '13.5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {company.clientName}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                  <div style={styles.companyStatus(company.isActive)}>
                    {company.isActive
                      ? <CheckCircle size={11} />
                      : <XCircle size={11} />}
                    {company.isActive ? 'Active' : 'Inactive'}
                  </div>
                  {company.subscriptionEndDate && (
                    <span style={{
                      fontSize: '11px',
                      color: theme === 'light' ? '#94a3b8' : 'rgba(255,255,255,0.4)',
                    }}>
                      · {new Date(company.subscriptionEndDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#2e7cba',
                  flexShrink: 0,
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompanyList;